<template>
  <div class="alipay-auth">
    <div class="auth-container">
      <div class="auth-header">
        <img class="logo-img" src="@assets/images/alipay.png" alt="支付宝" />
        <h1 class="title">支付宝账号授权</h1>
        <p class="subtitle">安全 · 便捷 · 快速绑定</p>
      </div>

      <div class="auth-body">
        <p class="desc">{{ descText }}</p>

        <ul class="feature-list">
          <li v-for="item in featureList" :key="item.text">
            <span class="feature-dot"></span>
            <span>{{ item.text }}</span>
          </li>
        </ul>

        <button class="auth-btn" :disabled="loading" @click="handleAuth">
          <span v-if="loading" class="btn-loading"></span>
          <span>{{ loading ? "正在准备..." : "立即授权" }}</span>
        </button>
        <p class="footer-tip">授权仅用于账号绑定，不会发起扣款</p>
      </div>
    </div>
  </div>
</template>

<script>
import { getAlipayAuthUrl, getAlipayAuthStatus } from "@api/user";
import { isWeixin } from "@utils";

export default {
  name: "AlipayAuth",
  data() {
    return {
      descText: "为了完成支付宝账号授权，请点击下方按钮跳转至支付宝完成授权登录。",
      isInWechat: false,
      loading: false,
      featureList: [
        { text: "跳转支付宝官方页面完成授权" },
        { text: "授权信息加密传输，保障账户安全" },
        { text: "授权完成后请返回当前应用" }
      ]
    };
  },
  mounted() {
    this.isInWechat = isWeixin();
  },
  methods: {
    handleAuth() {
      if (this.loading) return;
      this.loading = true;
      getAlipayAuthStatus()
        .then(res => {
          if (res.data && res.data.authorized) {
            this.loading = false;
            this.$dialog.confirm({
              title: "提示",
              mes: "您已授权过，是否重新授权？",
              opts: [
                {
                  txt: "取消",
                  color: false,
                  callback: () => {
                    this.$router.go(-1);
                  }
                },
                {
                  txt: "确定",
                  color: true,
                  callback: () => {
                    this.continueAuth();
                  }
                }
              ]
            });
            return;
          }
          this.continueAuth();
        })
        .catch(err => {
          this.loading = false;
          this.$dialog.error(err.msg || "获取授权状态失败");
        });
    },

    continueAuth() {
      this.loading = true;
      if (this.isInWechat) {
        this.goToLaunchPage();
        return;
      }
      this.invokeAlipayAuth();
    },

    goToLaunchPage() {
      getAlipayAuthUrl()
        .then(res => {
          const state = res.data && res.data.state;
          if (!state) {
            return Promise.reject({ msg: "未获取到授权会话，请重试" });
          }
          window.location.replace(`/user/alipay/auth/launch?state=${encodeURIComponent(state)}`);
        })
        .catch(err => {
          this.loading = false;
          this.$dialog.error(err.msg || "发起授权失败");
        });
    },

    invokeAlipayAuth() {
      getAlipayAuthUrl()
        .then(res => {
          const url = res.data && res.data.url;
          if (!url) {
            return Promise.reject({ msg: "未获取到授权跳转链接" });
          }
          window.location.replace(url);
        })
        .catch(err => {
          this.loading = false;
          this.$dialog.error(err.msg || "发起授权失败");
        });
    }
  }
};
</script>

<style scoped>
.alipay-auth {
  min-height: 100vh;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 10vh 16px 24px;
  background: linear-gradient(180deg, #e8f3ff 0%, #f5f7fa 42%, #f5f7fa 100%);
}

.auth-container {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(22, 119, 255, 0.12);
}

.auth-header {
  padding: 32px 24px 28px;
  text-align: center;
  color: #fff;
  background: linear-gradient(135deg, #1677ff 0%, #0958d9 100%);
}

.logo-img {
  width: 56px;
  height: 56px;
  margin: 0 auto 16px;
  display: block;
  object-fit: contain;
}

.title {
  margin: 0 0 8px;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.subtitle {
  margin: 0;
  font-size: 14px;
  opacity: 0.92;
}

.auth-body {
  padding: 28px 24px 32px;
}

.desc {
  margin: 0 0 20px;
  font-size: 15px;
  line-height: 1.7;
  color: #333;
  text-align: center;
}

.feature-list {
  margin: 0 0 28px;
  padding: 16px 18px;
  list-style: none;
  background: #f7faff;
  border: 1px solid #e6f0ff;
  border-radius: 12px;
}

.feature-list li {
  display: flex;
  align-items: flex-start;
  font-size: 14px;
  line-height: 1.6;
  color: #555;
}

.feature-list li + li {
  margin-top: 12px;
}

.feature-dot {
  flex-shrink: 0;
  width: 6px;
  height: 6px;
  margin: 8px 10px 0 0;
  border-radius: 50%;
  background: #1677ff;
}

.auth-btn {
  width: 100%;
  height: 48px;
  border: none;
  border-radius: 24px;
  background: linear-gradient(135deg, #1677ff 0%, #0958d9 100%);
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 8px 20px rgba(22, 119, 255, 0.28);
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.auth-btn:active:not(:disabled) {
  transform: scale(0.98);
}

.auth-btn:disabled {
  opacity: 0.75;
  cursor: not-allowed;
  box-shadow: none;
}

.btn-loading {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.wechat-notice {
  display: flex;
  align-items: flex-start;
  margin-top: 20px;
  padding: 12px 14px;
  background: #fff8e6;
  border: 1px solid #ffe7a3;
  border-radius: 10px;
}

.notice-icon {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-right: 10px;
  border-radius: 50%;
  background: #faad14;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
}

.wechat-notice p {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: #8c6d1f;
}

.footer-tip {
  margin: 18px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: #999;
  text-align: center;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
